export const BANOBAGI_RIGHT_TYPES = Object.freeze({
  SUPER_SYNC_ZONE: "super-sync-zone",
  WATSONS_EXCLUSIVE: "watsons-exclusive",
  SYNC_ZONE: "sync-zone"
});

export const BANOBAGI_RIGHT_DEFINITIONS = Object.freeze([
  {
    key: BANOBAGI_RIGHT_TYPES.SUPER_SYNC_ZONE,
    label: "SUPER Sync Zone",
    labelLines: ["SUPER Sync Zone"]
  },
  {
    key: BANOBAGI_RIGHT_TYPES.WATSONS_EXCLUSIVE,
    label: "SUPER Sync Zone (Watsons Exclusive)",
    labelLines: ["SUPER Sync Zone", "(Watsons Exclusive)"]
  },
  {
    key: BANOBAGI_RIGHT_TYPES.SYNC_ZONE,
    label: "Sync Zone",
    labelLines: ["Sync Zone"]
  }
]);

// Temporary mock product ids. Replace these values when the campaign SKU list is finalized.
export const BANOBAGI_RIGHT_RULES = Object.freeze({
  skuAProductIds: Object.freeze(["77"]),
  skuAMaxEligibleQuantity: 2,
  watsonsMinimumGrandTotal: 1990,
  otherChannelMinimumGrandTotal: 300,
  shopeeSyncZoneBonusPerUnit: 4
});

const RIGHT_DEFINITION_BY_KEY = new Map(
  BANOBAGI_RIGHT_DEFINITIONS.map(definition => [definition.key, definition])
);

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toFiniteNumber(value, fallback = 0) {
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "") : value;
  const number = Number(normalizedValue);
  return Number.isFinite(number) ? number : fallback;
}

function createRight(key, value) {
  const definition = RIGHT_DEFINITION_BY_KEY.get(key);
  return {
    key,
    label: definition?.label || key,
    value
  };
}

function resolveChannelText(transaction) {
  return normalizeText(
    [
      transaction?.branch_name,
      transaction?.branchName,
      transaction?.sales_channel_name,
      transaction?.sale_channel_name,
      transaction?.channel_name,
      transaction?.marketplace_name
    ]
      .filter(Boolean)
      .join(" ")
  );
}

function resolveProducts(transaction, detail) {
  const candidates = [
    detail?.transaction_details,
    detail?.products,
    transaction?._detail?.products,
    transaction?._banobagiDetail?.transaction_details,
    transaction?.transaction_details,
    transaction?.products,
    transaction?.product_list,
    transaction?.product
  ];

  return candidates.find(Array.isArray) || [];
}

function resolveProductId(product) {
  return String(
    product?.product_id ??
      product?.productId ??
      product?.sku_id ??
      product?.id ??
      ""
  );
}

function resolveGrandTotal(transaction, detail) {
  const candidates = [
    detail?.grand_total,
    detail?.grandTotal,
    transaction?.grand_total,
    transaction?.total_spend,
    transaction?._detail?.grandTotal,
    transaction?._banobagiDetail?.grand_total
  ];
  const value = candidates.find(
    candidate => candidate !== null && candidate !== undefined
  );
  return toFiniteNumber(value);
}

function resolveProductQuantity(product) {
  const value = [product?.quantity, product?.qty].find(
    candidate => candidate !== null && candidate !== undefined
  );

  if (value === undefined || value === "") return null;
  return Math.max(0, toFiniteNumber(value));
}

function sumProductQuantity(products, productIds, transaction) {
  const eligibleIds = new Set(productIds.map(String));
  const eligibleProducts = products.filter(product =>
    eligibleIds.has(resolveProductId(product))
  );

  if (eligibleProducts.length === 0) return 0;

  const total = eligibleProducts.reduce((sum, product) => {
    const qty = resolveProductQuantity(product);
    return sum + (qty ?? 1);
  }, 0);

  if (total > 0) return total;

  return Math.max(1, toFiniteNumber(transaction?.total_item, 1));
}

function resolveLegacyNegativeRights(transaction) {
  const candidates = [
    transaction?.right_details,
    transaction?.rights_summary,
    transaction?.fan_rights,
    transaction?.privilege_details
  ];
  const source = candidates.find(Array.isArray) || [transaction];
  const totals = createEmptyBanobagiRightsSummary();

  source.forEach(right => {
    const value = toFiniteNumber(
      right?.right_amount ??
        right?.right_quantity ??
        right?.amount ??
        right?.value,
      Number.NaN
    );
    if (!Number.isFinite(value) || value >= 0) return;

    const label = normalizeText(
      right?.fan_type_name || right?.name || right?.label || right?.right_name
    );
    if (label.includes("special") || label.includes("watsons exclusive")) {
      totals[BANOBAGI_RIGHT_TYPES.WATSONS_EXCLUSIVE] += value;
    } else if (label.includes("lucky") || label === "sync zone") {
      totals[BANOBAGI_RIGHT_TYPES.SYNC_ZONE] += value;
    } else if (label.includes("super")) {
      totals[BANOBAGI_RIGHT_TYPES.SUPER_SYNC_ZONE] += value;
    }
  });

  return BANOBAGI_RIGHT_DEFINITIONS.map(definition =>
    createRight(definition.key, totals[definition.key])
  ).filter(right => right.value < 0);
}

export function createEmptyBanobagiRightsSummary() {
  return BANOBAGI_RIGHT_DEFINITIONS.reduce((summary, definition) => {
    summary[definition.key] = 0;
    return summary;
  }, {});
}

const SUPER_SYNC_ZONE_BRANCH_KEYWORDS = [
  "bbt cosmetics flagship store",
  "bbt cosmetics",
  "banobagi"
];

export function isShopeeBbtTransaction(transaction) {
  const channel = resolveChannelText(transaction);
  const matchesShopeeCombo =
    channel.includes("shopee") &&
    (channel.includes("bbt") || channel.includes("banobagi"));
  const matchesBranchName = SUPER_SYNC_ZONE_BRANCH_KEYWORDS.some(keyword =>
    channel.includes(normalizeText(keyword))
  );
  return matchesShopeeCombo || matchesBranchName;
}

export function isWatsonsTransaction(transaction) {
  const channel = resolveChannelText(transaction);
  return (
    channel.includes("watson") ||
    channel.includes("วัตสัน") ||
    channel.includes("Watson")
  );
}

export function isOtherMarketplaceOrOfflineTransaction(transaction) {
  if (
    isShopeeBbtTransaction(transaction) ||
    isWatsonsTransaction(transaction)
  ) {
    return false;
  }

  return resolveChannelText(transaction).length > 0;
}

function isCancelledTransaction(transaction) {
  console.log(transaction);
  const statusId = transaction?.user_transaction_status_id;
  if (statusId === undefined || statusId === null || statusId === "") {
    return false;
  }
  return Number(statusId) !== 1;
}

function applyCancellationSign(rights, transaction) {
  if (!isCancelledTransaction(transaction)) return rights;
  console.log(rights);
  return rights.map(right => ({
    ...right,
    value: -Math.abs(toFiniteNumber(right.value))
  }));
}

export function calculateBanobagiTransactionRights(
  transaction,
  detail = null,
  rules = BANOBAGI_RIGHT_RULES
) {
  const negativeRights = resolveLegacyNegativeRights(transaction);
  if (negativeRights.length > 0) return negativeRights;

  const products = resolveProducts(transaction, detail);
  const grandTotal = Math.abs(resolveGrandTotal(transaction, detail));

  const isShopee = isShopeeBbtTransaction(transaction);

  if (isShopee) {
    const skuAQuantity = sumProductQuantity(
      products,
      rules.skuAProductIds,
      transaction
    );
    if (skuAQuantity >= 1) {
      const eligibleQuantity = Math.min(
        skuAQuantity,
        rules.skuAMaxEligibleQuantity
      );
      return applyCancellationSign(
        [
          createRight(BANOBAGI_RIGHT_TYPES.SUPER_SYNC_ZONE, eligibleQuantity),
          createRight(
            BANOBAGI_RIGHT_TYPES.SYNC_ZONE,
            rules.shopeeSyncZoneBonusPerUnit * eligibleQuantity
          )
        ],
        transaction
      );
    }
  }

  if (
    isWatsonsTransaction(transaction) &&
    grandTotal >= rules.watsonsMinimumGrandTotal
  ) {
    return applyCancellationSign(
      [createRight(BANOBAGI_RIGHT_TYPES.WATSONS_EXCLUSIVE, 1)],
      transaction
    );
  }

  const isEligibleForOtherChannel =
    isOtherMarketplaceOrOfflineTransaction(transaction) ||
    isShopee ||
    isWatsonsTransaction(transaction);

  if (
    isEligibleForOtherChannel &&
    grandTotal >= rules.otherChannelMinimumGrandTotal
  ) {
    const syncZoneUnits = Math.floor(
      grandTotal / rules.otherChannelMinimumGrandTotal
    );
    return applyCancellationSign(
      [createRight(BANOBAGI_RIGHT_TYPES.SYNC_ZONE, syncZoneUnits)],
      transaction
    );
  }

  return [];
}

export function summarizeBanobagiRights(transactions) {
  return transactions.reduce((summary, transaction) => {
    const rights = Array.isArray(transaction?._rights)
      ? transaction._rights
      : calculateBanobagiTransactionRights(
          transaction,
          transaction?._banobagiDetail || null
        );

    rights.forEach(right => {
      if (!Object.prototype.hasOwnProperty.call(summary, right.key)) return;
      summary[right.key] += toFiniteNumber(right.value);
    });
    return summary;
  }, createEmptyBanobagiRightsSummary());
}
